import React from 'react';

// Components
import { Button } from '@capintel/component-lib/button';
import { Dates, Tabs } from './components';

// Libraries
import moment from 'moment';

// Types
import HistoricalScenario from '@/types/historical-scenario';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';

// Utils
import { formatMessage, generateLocalizedStringFromText, LOCALE } from '@/utils/i18n';

interface UpsertFormProps {
  customScenario?: Nullable<HistoricalScenario>;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (scenario: Partial<HistoricalScenario>) => void;
}

export const UpsertForm: React.FC<UpsertFormProps> = ({ customScenario, isLoading, onClose, onSubmit }) => {
  const i18nBasePath = 'Comparison.HistoricalScenarios.UpsertForm';

  const [fromDate, setFromDate] = React.useState<Date | undefined>(customScenario ? moment(customScenario.startDate).toDate() : undefined);
  const [infoFields, setInfoFields] = React.useState<Partial<HistoricalScenario>>({
    description: customScenario ? customScenario.description : generateLocalizedStringFromText(''),
    name: customScenario ? customScenario.name : generateLocalizedStringFromText(''),
    shortDescription: customScenario ? customScenario.shortDescription : generateLocalizedStringFromText(''),
  });
  const [selectedTab, setSelectedTab] = React.useState<Locale>(LOCALE.EN_CA);
  const [toDate, setToDate] = React.useState<Date | undefined>(customScenario ? moment(customScenario.endDate).toDate() : undefined);

  const handleOnSubmit = () => {
    const formattedScenario = {
      ...(customScenario || {}),
      description: {
        ...infoFields.description,
        [LOCALE.EN_US]: infoFields.description?.[LOCALE.EN_CA] ?? '',
      },
      endDate: toDate,
      name: {
        ...infoFields.name,
        [LOCALE.EN_US]: infoFields.name?.[LOCALE.EN_CA] ?? '',
      },
      shortDescription: {
        ...infoFields.shortDescription,
        [LOCALE.EN_US]: infoFields.shortDescription?.[LOCALE.EN_CA] ?? '',
      },
      startDate: fromDate,
    };
    onSubmit(formattedScenario);
  };

  const validateIfSubmitIsDisabled = () => {
    const { description, name, shortDescription } = infoFields;
    const hasSpecificDates = fromDate && toDate;
    const hasScenarioInfo = description?.[selectedTab] && name?.[selectedTab] && shortDescription?.[selectedTab];
    return !(hasSpecificDates && hasScenarioInfo);
  };

  return (
    <div className="upsert-form">
      <Dates fromDate={fromDate} i18nBasePath={i18nBasePath} loading={isLoading} onSetFromDate={setFromDate} onSetToDate={setToDate} toDate={toDate} />
      <Tabs i18nBasePath={i18nBasePath} infoFields={infoFields} loading={isLoading} onSelectTab={setSelectedTab} onUpdateFields={setInfoFields} />
      <div className="upsert-form-actions">
        <Button variant="secondary" onClick={onClose}>
          {formatMessage(`${i18nBasePath}.Cancel`)}
        </Button>
        <Button variant="primary" onClick={handleOnSubmit} disabled={validateIfSubmitIsDisabled()} loading={isLoading}>
          {formatMessage(`${i18nBasePath}.${isLoading ? 'Saving' : 'Save'}`)}
        </Button>
      </div>
    </div>
  );
};
